import React, { useMemo } from 'react'
import { Box, Tooltip, Typography, Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import InsightsIcon from '@mui/icons-material/Insights'
import { formatDisplayName } from '../../../../utils/display-name-formatter'

interface ThresholdData {
  threshold_name: string
  lr_pos_index: number
  lr_neg_index: number
}

interface ConditionAssessment {
  condition_name: string
  condition_display_name: string
  programs?: string[]
  thresholds?: ThresholdData[]
  // Direct PLR/NLR for threshold cards (cond_per_threshold)
  lr_pos_index?: number
  lr_neg_index?: number
  sensitivity?: number
  specificity?: number
}

interface Props {
  condPerAssessments: ConditionAssessment[]
}

const DiagnosticImpactIndicator: React.FC<Props> = ({ condPerAssessments }) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)

  const patientPrograms = useMemo(() => {
    return patientDetail?.program_enrollments?.map((e: any) => e.program) || []
  }, [patientDetail?.program_enrollments])

  // Separate conditions into program-matched and non-program
  const { programConditions, nonProgramConditions, sortedConditions } = useMemo(() => {
    if (!condPerAssessments?.length) return { programConditions: [], nonProgramConditions: [], sortedConditions: [] }

    const programMatched: ConditionAssessment[] = []
    const nonProgram: ConditionAssessment[] = []

    condPerAssessments.forEach(c => {
      if (c.programs?.some(p => patientPrograms.includes(p))) {
        programMatched.push(c)
      } else {
        nonProgram.push(c)
      }
    })

    // Sort each group alphabetically
    const sortFn = (a: ConditionAssessment, b: ConditionAssessment) =>
      (a.condition_display_name || a.condition_name || '').localeCompare(
        b.condition_display_name || b.condition_name || ''
      )
    programMatched.sort(sortFn)
    nonProgram.sort(sortFn)

    return {
      programConditions: programMatched,
      nonProgramConditions: nonProgram,
      sortedConditions: [...programMatched, ...nonProgram]
    }
  }, [condPerAssessments, patientPrograms])

  const programCount = programConditions.length
  const nonProgramCount = nonProgramConditions.length

  if (!condPerAssessments?.length) return null

  // Render PLR bar (green) - scale: 0-2, values >= 2 show as full bar
  const renderPlrBar = (plr: number) => {
    // Scale: 0 = 0%, 2+ = 100%
    const width = Math.min(Math.max((plr / 2) * 100, 0), 100)
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" sx={{ fontSize: '0.55rem', width: '22px', color: 'var(--color-text-secondary)' }}>
          PLR
        </Typography>
        <Box sx={{ width: 50, height: 5, backgroundColor: 'var(--color-border-medium)', borderRadius: 1 }}>
          <Box
            sx={{
              width: `${width}%`,
              height: '100%',
              backgroundColor: '#4caf50',
              borderRadius: 1,
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.55rem', minWidth: '24px', color: 'var(--color-text-secondary)' }}>
          {plr.toFixed(1)}
        </Typography>
      </Stack>
    )
  }

  // Render NLR bar (blue) - scale: 0 to -2, values <= -2 show as full bar
  const renderNlrBar = (nlr: number) => {
    // Scale: 0 = 0%, -2 or lower = 100%
    const width = Math.min(Math.max((Math.abs(nlr) / 2) * 100, 0), 100)
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" sx={{ fontSize: '0.55rem', width: '22px', color: 'var(--color-text-secondary)' }}>
          NLR
        </Typography>
        <Box sx={{ width: 50, height: 5, backgroundColor: 'var(--color-border-medium)', borderRadius: 1 }}>
          <Box
            sx={{
              width: `${width}%`,
              height: '100%',
              backgroundColor: '#2196f3',
              borderRadius: 1,
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.55rem', minWidth: '24px', color: 'var(--color-text-secondary)' }}>
          {nlr.toFixed(1)}
        </Typography>
      </Stack>
    )
  }

  const renderCondition = (condition: ConditionAssessment, index: number) => {
    const isProgramMatch = condition.programs?.some(p => patientPrograms.includes(p)) || false
    const displayName = condition.condition_display_name || condition.condition_name || 'Unknown'

    // Check if condition has direct PLR/NLR (threshold cards) or nested thresholds (questionnaire cards)
    const hasDirectPlrNlr = condition.lr_pos_index !== undefined || condition.lr_neg_index !== undefined
    const hasNestedThresholds = condition.thresholds && condition.thresholds.length > 0

    return (
      <Box key={`condition-${index}`} sx={{ mb: 0.75 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isProgramMatch && (
            <Typography sx={{ color: '#1976d2', fontSize: '0.65rem' }}>★</Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.65rem',
              fontWeight: isProgramMatch ? 600 : 400,
              color: isProgramMatch ? '#1976d2' : '#333',
            }}
          >
            {displayName}
          </Typography>
        </Stack>
        {/* Show PLR/NLR directly if available (threshold cards) */}
        {hasDirectPlrNlr && (
          <Box sx={{ pl: isProgramMatch ? 1.5 : 0, mt: 0.25 }}>
            <Box sx={{ pl: 0 }}>
              {renderPlrBar(condition.lr_pos_index || 0)}
              {renderNlrBar(condition.lr_neg_index || 0)}
            </Box>
          </Box>
        )}
        {/* Show each threshold with its evidence name and PLR/NLR (questionnaire cards) */}
        {hasNestedThresholds && condition.thresholds?.map((threshold, tIndex) => (
          <Box key={`threshold-${tIndex}`} sx={{ pl: isProgramMatch ? 1.5 : 0, mt: 0.25 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.55rem', color: 'var(--color-text-secondary)', display: 'block', mb: 0.25 }}
            >
              - {formatDisplayName(threshold.threshold_name)}
            </Typography>
            <Box sx={{ pl: 1 }}>
              {renderPlrBar(threshold.lr_pos_index || 0)}
              {renderNlrBar(threshold.lr_neg_index || 0)}
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  const tooltipContent = (
    <Box sx={{ p: 0.5, maxWidth: 380, maxHeight: 400, overflowY: 'auto' }}>
      <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>
        Diagnostic Impact
      </Typography>
      {/* Program conditions section */}
      {programConditions.length > 0 && (
        <Box sx={{ borderTop: '1px solid #ddd', pt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#1976d2', display: 'block', mb: 0.5 }}
          >
            Program Conditions ({programCount})
          </Typography>
          {programConditions.map((condition, index) => renderCondition(condition, index))}
        </Box>
      )}
      {/* Non-program conditions section */}
      {nonProgramConditions.length > 0 && (
        <Box sx={{ borderTop: '1px solid #ddd', pt: 0.5, mt: programConditions.length > 0 ? 0.5 : 0 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#757575', display: 'block', mb: 0.5 }}
          >
            Impacted Conditions ({nonProgramCount})
          </Typography>
          {nonProgramConditions.map((condition, index) => renderCondition(condition, index))}
        </Box>
      )}
    </Box>
  )

  // Render individual badge
  const renderBadge = (count: number, isProgram: boolean) => {
    if (count === 0) return null
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 0.5,
          py: 0.1,
          borderRadius: 1,
          backgroundColor: isProgram ? '#e3f2fd' : '#f5f5f5',
        }}
      >
        <InsightsIcon
          sx={{
            fontSize: '0.7rem',
            color: isProgram ? '#1976d2' : '#757575',
            mr: 0.25,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.55rem',
            fontWeight: 500,
            color: isProgram ? '#1976d2' : '#757575',
          }}
        >
          {count}
        </Typography>
      </Box>
    )
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'var(--color-paper)',
            color: 'var(--color-text-primary)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid var(--color-border-light)',
            maxWidth: 400,
            '& .MuiTooltip-arrow': {
              color: 'var(--color-paper)',
              '&::before': {
                border: '1px solid var(--color-border-light)',
              },
            },
          },
        },
      }}
    >
      <Stack
        direction="row"
        spacing={0.25}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
          ml: 0.5,
          '&:hover': {
            '& .MuiBox-root': {
              filter: 'brightness(0.95)',
            },
          },
        }}
      >
        {renderBadge(programCount, true)}
        {renderBadge(nonProgramCount, false)}
      </Stack>
    </Tooltip>
  )
}

export default DiagnosticImpactIndicator
